import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, notification, Space, Table, Tooltip } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import Column from '../../helpers/columns';
import { getDocTypes, getUserTypes } from '../../services/taxonomies';
import useDictionaries from '../../hooks/useDictionaries';
import { FormContext } from '../../hooks/useForm';
import DeleteItemIcon from '../buttons/DeleteItemIcon';
import FormWrapper from '../FormWrapper';
import {
  BASE_URI as FILE_BASE_URI,
  downloadFilesById,
  SERVICE_URI as FILE_SERVICE_URI,
} from '../../services/files';
import { getProducts } from '../../services/products';
import SearchSubdivisions from './SearchSubdivisions';
import { Environments } from '../../helpers/constants';
import { BULK_TYPES } from '../../pages/orders/constants';

const dictionaries = {
  docTypesTaxonomy: getDocTypes,
  userTypes: getUserTypes,
  productTypes: getProducts,
};

const ProductUsersList = ({ index, disabled, fields, institutionId }) => {
  const { t } = useTranslation();
  const form = useContext(FormContext);
  const [documentTypes, setDocTypes] = useState([]);
  const environment = useMemo(() => window._env_.ENVIROMENT, []);
  const [subdivisionIdTranslate, setSubdivisionIdTranslate] = useState(null);

  // eslint-disable-next-line no-unused-vars
  const [{ docTypesTaxonomy, userTypes, productTypes }] =
    useDictionaries(dictionaries);

  const { input, value, setValue, custom, checkbox } = form;

  const { products = [], id: orderId, bulkType, cardCount } = value;
  const { productPersons = [], productId } = products[index];

  const onRemove = useCallback(
    async (record) => {
      const newProducts = products.map((product) => ({
        ...product,
        productPersons: product.productPersons.filter((pp) =>
          record.key
            ? pp.person.key !== record.key
            : pp.person.id !== record.id,
        ),
      }));
      setValue('products', newProducts);
      if (BULK_TYPES.ISSUING_NON_NOMINAL_CARDS === bulkType) {
        const currentCount = parseInt(cardCount, 10) || 0;
        setValue('cardCount', Math.max(0, currentCount - 1));
      }
    },
    [setValue, products, bulkType, cardCount],
  );

  const onUploadFailed = useCallback(
    (msg) => {
      const messageCode = msg?.inner?._;
      notification.error({
        message: messageCode
          ? t(messageCode)
          : 'A aparut o eroare, incercati din nou',
      });
    },
    [t],
  );

  useEffect(() => {
    switch (environment) {
      case Environments.PITESTI:
        setSubdivisionIdTranslate(t('entity.admin.user.subdivisionIdPitesti'));
        break;
      default:
        setSubdivisionIdTranslate(t('entity.admin.user.subdivisionId'));
    }
  }, [environment, t]);

  useEffect(() => {
    const { userTypeId } =
      // eslint-disable-next-line eqeqeq
      productTypes.content.find((pt) => pt.id == productId) || {};
    // eslint-disable-next-line eqeqeq
    const type = userTypes.content.find((c) => c.id == userTypeId);
    const docs = type?.docTypes || [];

    const res = docTypesTaxonomy.content.filter((dt) => docs.includes(dt.id));
    setDocTypes(res.sort((a, b) => a.id - b.id));
  }, [docTypesTaxonomy.content, productId, productTypes, userTypes.content]);

  const getDocColumns = useCallback(() => {
    return documentTypes.map((dt, docIndex) =>
      Column.actions(dt.name, (record, rowIndex) => {
        const currentDoc = record?.person?.documents?.find(
          (savedDoc) => savedDoc && savedDoc.documentTypeId === dt.id,
        );
        return (
          <>
            <FormWrapper.FileUploader
              props={{
                disabled,
                multiple: false,
                uploadUrl: `${FILE_SERVICE_URI}${FILE_BASE_URI}/${orderId}/files`,
                ...custom(
                  `products[${index}].productPersons[${rowIndex}].person.documents[${docIndex}]`,
                ),
                setValue: (name, uploadedFiles) => {
                  setValue(name, {
                    fileId: uploadedFiles[0].id,
                    documentTypeId: dt.id,
                  });
                },
                setFieldValue: setValue,
                onUploadFailed,
              }}
            />

            {currentDoc && (
              <Space style={{ paddingLeft: '1rem' }}>
                <Tooltip title={t('actions.orders.viewDoc')}>
                  <Button
                    shape="circle"
                    type="link"
                    icon={<FileTextOutlined />}
                    style={{ color: '#24ad09' }}
                    onClick={() => downloadFilesById(currentDoc.fileId)}
                  />
                </Tooltip>
              </Space>
            )}
          </>
        );
      }),
    );
  }, [
    custom,
    disabled,
    documentTypes,
    index,
    orderId,
    setValue,
    t,
    onUploadFailed,
  ]);

  const columns = useMemo(
    () => [
      ...fields
        .filter((it) => it !== 'subdivisionId')
        .map((name) =>
          name !== 'foreignCitizen'
            ? {
                title: t(`entity.admin.user.${name}`),
                dataIndex: name,
                key: name,
                sorter: true,
                width: 150,
                render: (_, record, idx) => (
                  <FormWrapper.Input
                    props={{
                      ...input(
                        `products[${index}].productPersons[${idx}].person.${name}`,
                      ),
                      disabled:
                        BULK_TYPES.ISSUING_NON_NOMINAL_CARDS === bulkType ||
                        disabled,
                      ...(name === 'fullname'
                        ? {
                            onChange: (e) => {
                              const cursorPosition = e.target.selectionStart;
                              const newText =
                                e.target.value.substring(
                                  0,
                                  cursorPosition - 1,
                                ) +
                                (cursorPosition > 0
                                  ? e.target.value[
                                      cursorPosition - 1
                                    ].toUpperCase()
                                  : '') +
                                e.target.value.substring(cursorPosition);

                              setValue(
                                `products[${index}].productPersons[${idx}].person.fullname`,
                                newText.toUpperCase(),
                              );

                              requestAnimationFrame(() => {
                                e.target.setSelectionRange(
                                  cursorPosition,
                                  cursorPosition,
                                );
                              });
                            },
                          }
                        : {}),
                    }}
                  />
                ),
              }
            : {
                title: t('entity.admin.user.foreignCitizen'),
                dataIndex: 'foreignCitizen',
                key: 'foreignCitizen',
                sorter: true,
                width: 150,
                render: (_, record, idx) => (
                  <FormWrapper.Switch
                    props={{
                      disabled,
                      searchField: 'name',
                      field: 'id',
                      institutionId,
                      ...checkbox(
                        `products[${index}].productPersons[${idx}].person.foreignCitizen`,
                      ),
                    }}
                  />
                ),
              },
        ),
      ...(fields.includes('subdivisionId')
        ? [
            {
              title: subdivisionIdTranslate,
              dataIndex: 'subdivisionId',
              key: 'subdivisionId',
              sorter: true,
              width: 150,
              render: (_, record, idx) => (
                <SearchSubdivisions
                  props={{
                    disabled,
                    searchField: 'name',
                    field: 'id',
                    institutionId,
                    ...custom(
                      `products[${index}].productPersons[${idx}].person.subdivisionId`,
                    ),
                  }}
                />
              ),
            },
          ]
        : []),
      ...getDocColumns(),
      Column.actions(t('table.actions'), (record) => (
        <DeleteItemIcon
          title={t('entity.admin.user._delete', record.person)}
          message={t('entity.admin.user._deleted', record.person)}
          item={record.person}
          reload={() => null}
          action={onRemove}
          disabled={disabled}
        />
      )),
    ],
    [
      fields,
      subdivisionIdTranslate,
      getDocColumns,
      t,
      input,
      index,
      disabled,
      setValue,
      institutionId,
      checkbox,
      custom,
      onRemove,
      bulkType,
    ],
  );

  return (
    <Table
      columns={columns}
      rowKey={(record) => record.key}
      pagination={false}
      dataSource={productPersons}
    />
  );
};

export default ProductUsersList;
